import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import type { ReactNode } from 'react';
import type { CrudFilter, CrudSort } from '@refinedev/core';
import type { Dayjs } from 'dayjs';
import { emptyFunction } from '@admin-panel/utils';

export type DiscoveryFilterType =
  | 'TEXT'
  | 'SINGLE_SELECT'
  | 'MULTISELECT'
  | 'DATE_RANGE'
  | 'RESOURCE_SELECT'
  | 'CUSTOM';
export type DiscoveryApplyMode = 'manual' | 'immediate';
export type DiscoveryLayout = 'mobile' | 'desktop';

export interface DiscoveryOption {
  label: ReactNode;
  value: string | number;
  count?: number;
}

export interface DiscoverySorterOption {
  key: string;
  label: ReactNode;
  sorters: Array<CrudSort>;
}

export interface DiscoveryFilterRenderProps {
  value: DiscoveryFilterValue | undefined;
  values: DiscoveryValueMap;
  setValue: (value: DiscoveryFilterValue | undefined) => void;
  clearValue: () => void;
  layout: DiscoveryLayout;
  popupContainer: HTMLElement | null;
}

export interface DiscoveryResourceSelectConfig {
  resource: string;
  labelField: string;
  valueField: string;
  mode?: 'single' | 'multiple';
  searchField?: string;
  searchOperator?: string;
  filters?: Array<CrudFilter>;
  sorters?: Array<CrudSort>;
  pageSize?: number;
  helperText?: ReactNode;
  disabledWhen?: (values: DiscoveryValueMap) => boolean;
}

export interface DiscoveryDateRangeValue {
  from?: Dayjs | null;
  to?: Dayjs | null;
}

export type DiscoveryPrimitive = string | number | boolean | null | undefined;

export type DiscoveryFilterValue =
  | Array<DiscoveryOption>
  | DiscoveryDateRangeValue
  | DiscoveryPrimitive
  | Array<DiscoveryPrimitive>
  | Record<string, unknown>;

export type DiscoveryValueMap = Record<string, DiscoveryFilterValue | undefined>;

export interface DiscoveryApplyResult {
  rawValues: DiscoveryValueMap;
  values: Record<string, unknown>;
  crudFilters: Array<CrudFilter>;
  crudSorters: Array<CrudSort>;
  sorterKey: string | null;
}

export type DiscoveryValueTransformer = (
  value: DiscoveryFilterValue | undefined,
  values: DiscoveryValueMap,
) => unknown;

export interface DiscoveryFilter {
  key: string;
  label: string;
  type: DiscoveryFilterType;
  placeholder?: string;
  description?: string;
  options?: Array<DiscoveryOption>;
  shouldSortAlphabetically?: boolean;
  shouldShowSelectAll?: boolean;
  shouldCategorizeAlphabetically?: boolean;
  resourceConfig?: DiscoveryResourceSelectConfig;
  renderValue?: (props: DiscoveryFilterRenderProps) => ReactNode;
  transformValue?: DiscoveryValueTransformer;
  crudField?: string;
  crudOperator?: CrudFilter['operator'];
  buildCrudFilters?: (value: unknown, values: DiscoveryValueMap) => Array<CrudFilter>;
}

interface DiscoveryState {
  selectedFilterKey: string | null;
  pendingValues: DiscoveryValueMap;
  appliedValues: DiscoveryValueMap;
  pendingSorterKey: string | null;
  appliedSorterKey: string | null;
}

type DiscoveryAction =
  | { type: 'SELECT_FILTER'; payload: { filterKey: string | null } }
  | {
      type: 'SET_PENDING_VALUE';
      payload: {
        filterKey: string;
        value: DiscoveryFilterValue | undefined;
        syncApplied?: boolean;
      };
    }
  | { type: 'CLEAR_FILTER'; payload: { filterKey: string; syncApplied?: boolean } }
  | { type: 'CLEAR_ALL'; payload?: { syncApplied?: boolean; resetSorterKey?: string | null } }
  | { type: 'APPLY_PENDING' }
  | {
      type: 'SET_PENDING_SORTER';
      payload: { sorterKey: string | null; syncApplied?: boolean };
    }
  | {
      type: 'RESET';
      payload: {
        selectedFilterKey: string | null;
        initialValues?: DiscoveryValueMap;
        initialSorterKey?: string | null;
      };
    };

const cloneFilterValue = (
  value: DiscoveryFilterValue | undefined,
): DiscoveryFilterValue | undefined => {
  if (Array.isArray(value)) {
    return [...value] as DiscoveryFilterValue;
  }

  if (value && typeof value === 'object') {
    return { ...(value as Record<string, unknown>) };
  }

  return value;
};

const cloneValueMap = (valueMap?: DiscoveryValueMap): DiscoveryValueMap => {
  if (!valueMap) {
    return {};
  }

  return Object.entries(valueMap).reduce<DiscoveryValueMap>((acc, [key, value]) => {
    acc[key] = cloneFilterValue(value);
    return acc;
  }, {});
};

const defaultTransformers: Partial<Record<DiscoveryFilterType, DiscoveryValueTransformer>> = {
  TEXT: value => {
    if (typeof value !== 'string') {
      return undefined;
    }
    const trimmed = value.trim();
    return trimmed || undefined;
  },
  SINGLE_SELECT: value => {
    if (!Array.isArray(value) || value.length === 0) {
      return undefined;
    }
    const firstOption = value[0] as DiscoveryOption;
    return firstOption?.value;
  },
  MULTISELECT: value => {
    if (!Array.isArray(value) || value.length === 0) {
      return undefined;
    }
    return (value as Array<DiscoveryOption>).map(option => option.value);
  },
  DATE_RANGE: value => value,
  RESOURCE_SELECT: value => value,
};

const omitKey = (valueMap: DiscoveryValueMap, key: string): DiscoveryValueMap => {
  const { [key]: _, ...rest } = valueMap;
  return rest;
};

const createInitialState = (
  selectedFilterKey: string | null,
  initialValues?: DiscoveryValueMap,
  initialSorterKey?: string | null,
): DiscoveryState => ({
  selectedFilterKey,
  pendingValues: cloneValueMap(initialValues),
  appliedValues: cloneValueMap(initialValues),
  pendingSorterKey: initialSorterKey ?? null,
  appliedSorterKey: initialSorterKey ?? null,
});

const discoveryReducer = (state: DiscoveryState, action: DiscoveryAction): DiscoveryState => {
  switch (action.type) {
    case 'SELECT_FILTER':
      return { ...state, selectedFilterKey: action.payload.filterKey };
    case 'SET_PENDING_VALUE': {
      const { filterKey, value, syncApplied } = action.payload;
      const pendingValues =
        value === undefined
          ? omitKey(state.pendingValues, filterKey)
          : { ...state.pendingValues, [filterKey]: value };

      return {
        ...state,
        pendingValues,
        appliedValues: syncApplied ? cloneValueMap(pendingValues) : state.appliedValues,
      };
    }
    case 'CLEAR_FILTER': {
      const pendingValues = omitKey(state.pendingValues, action.payload.filterKey);
      return {
        ...state,
        pendingValues,
        appliedValues: action.payload.syncApplied
          ? cloneValueMap(pendingValues)
          : state.appliedValues,
      };
    }
    case 'CLEAR_ALL': {
      const pendingValues: DiscoveryValueMap = {};
      const resetSorterKey = action.payload?.resetSorterKey ?? state.pendingSorterKey;
      return {
        ...state,
        pendingValues,
        pendingSorterKey: resetSorterKey,
        appliedValues: action.payload?.syncApplied ? {} : state.appliedValues,
        appliedSorterKey: action.payload?.syncApplied ? resetSorterKey : state.appliedSorterKey,
      };
    }
    case 'APPLY_PENDING':
      return {
        ...state,
        appliedValues: cloneValueMap(state.pendingValues),
        appliedSorterKey: state.pendingSorterKey,
      };
    case 'SET_PENDING_SORTER':
      return {
        ...state,
        pendingSorterKey: action.payload.sorterKey,
        appliedSorterKey: action.payload.syncApplied
          ? action.payload.sorterKey
          : state.appliedSorterKey,
      };
    case 'RESET':
      return createInitialState(
        action.payload.selectedFilterKey,
        action.payload.initialValues,
        action.payload.initialSorterKey,
      );
    default:
      return state;
  }
};

const resolveSelectedFilterKey = (
  filters: Array<DiscoveryFilter>,
  preferredName?: string,
): string | null => {
  if (preferredName && filters.some(filter => filter.key === preferredName)) {
    return preferredName;
  }

  return filters[0]?.key ?? null;
};

const resolveSelectedSorterKey = (
  sorterOptions?: Array<DiscoverySorterOption>,
  preferredKey?: string | null,
): string | null => {
  if (!sorterOptions?.length) {
    return null;
  }

  if (preferredKey && sorterOptions.some(option => option.key === preferredKey)) {
    return preferredKey;
  }

  return sorterOptions[0]?.key ?? null;
};

interface DiscoveryProviderProps {
  filters: Array<DiscoveryFilter>;
  children: ReactNode;
  initialSelectedFilter?: string;
  initialValues?: DiscoveryValueMap;
  sorterOptions?: Array<DiscoverySorterOption>;
  initialSorterKey?: string | null;
  onApply?: (payload: DiscoveryApplyResult) => void;
  onClearAll?: () => void;
  onTabChange?: (tab: string, values: DiscoveryValueMap) => void;
  applyMode?: DiscoveryApplyMode;
  layout: DiscoveryLayout;
}

interface DiscoveryContextValue extends DiscoveryState {
  filters: Array<DiscoveryFilter>;
  sorterOptions?: Array<DiscoverySorterOption>;
  applyMode: DiscoveryApplyMode;
  layout: DiscoveryLayout;
  setPendingValue: (filterKey: string, value: DiscoveryFilterValue | undefined) => void;
  setPendingSorter: (sorterKey: string | null) => void;
  selectFilter: (filterKey: string) => void;
  clearFilter: (filterKey: string) => void;
  clearAll: () => void;
  applyFilters: () => void;
}

const buildTransformedValues = (
  filters: Array<DiscoveryFilter>,
  values: DiscoveryValueMap,
): Record<string, unknown> =>
  filters.reduce<Record<string, unknown>>((acc, filter) => {
    if (!(filter.key in values)) {
      return acc;
    }

    const rawValue = values[filter.key];
    const transformer = filter.transformValue ?? defaultTransformers[filter.type];
    const transformed = transformer ? transformer(rawValue, values) : rawValue;

    if (transformed !== undefined) {
      acc[filter.key] = transformed;
    }

    return acc;
  }, {});

const buildApplyPayload = (
  filters: Array<DiscoveryFilter>,
  values: DiscoveryValueMap,
  sorterOptions?: Array<DiscoverySorterOption>,
  sorterKey?: string | null,
): DiscoveryApplyResult => {
  const transformedValues = buildTransformedValues(filters, values);
  const matchedSorter = sorterKey
    ? sorterOptions?.find(option => option.key === sorterKey)
    : undefined;
  const crudSorters = matchedSorter?.sorters ?? [];

  const crudFilters = filters.reduce<Array<CrudFilter>>((acc, filter) => {
    if (!(filter.key in values)) {
      return acc;
    }

    if (filter.buildCrudFilters) {
      acc.push(...(filter.buildCrudFilters(values[filter.key], values) ?? []));
      return acc;
    }

    if (filter.type === 'CUSTOM') {
      return acc;
    }

    const field = filter.crudField ?? filter.key;
    const raw = transformedValues[filter.key];

    if (raw === undefined) {
      return acc;
    }

    if (Array.isArray(raw)) {
      if (!raw.length) {
        return acc;
      }

      acc.push({
        field,
        operator: filter.crudOperator ?? 'in',
        value: raw,
      } as CrudFilter);
      return acc;
    }

    acc.push({
      field,
      operator: filter.crudOperator ?? 'eq',
      value: raw,
    } as CrudFilter);

    return acc;
  }, []);

  return {
    rawValues: cloneValueMap(values),
    values: transformedValues,
    crudFilters,
    crudSorters,
    sorterKey: sorterKey ?? null,
  };
};

export const DiscoveryContext = createContext<DiscoveryContextValue | undefined>(undefined);

const fallbackContext: DiscoveryContextValue = {
  selectedFilterKey: null,
  pendingValues: {},
  appliedValues: {},
  pendingSorterKey: null,
  appliedSorterKey: null,
  filters: [],
  sorterOptions: [],
  applyMode: 'manual',
  layout: 'desktop',
  setPendingValue: emptyFunction,
  setPendingSorter: emptyFunction,
  selectFilter: emptyFunction,
  clearFilter: emptyFunction,
  clearAll: emptyFunction,
  applyFilters: emptyFunction,
};

export const DiscoveryProvider = ({
  children,
  filters,
  initialSelectedFilter,
  initialValues,
  sorterOptions,
  initialSorterKey,
  onApply,
  onClearAll,
  onTabChange,
  applyMode = 'manual',
  layout,
}: DiscoveryProviderProps) => {
  const resolvedSelectedFilter = resolveSelectedFilterKey(filters, initialSelectedFilter);
  const resolvedSorterKey = resolveSelectedSorterKey(sorterOptions, initialSorterKey);

  const [state, dispatch] = useReducer(
    discoveryReducer,
    createInitialState(resolvedSelectedFilter, initialValues, resolvedSorterKey),
  );

  const shouldSyncApplied = applyMode === 'immediate';
  const hasAutoAppliedRef = useRef(false);

  useEffect(() => {
    dispatch({
      type: 'RESET',
      payload: {
        selectedFilterKey: resolveSelectedFilterKey(filters, initialSelectedFilter),
        initialValues,
        initialSorterKey: resolveSelectedSorterKey(sorterOptions, initialSorterKey),
      },
    });
    hasAutoAppliedRef.current = false;
  }, [filters, initialSelectedFilter, initialValues, sorterOptions, initialSorterKey]);

  useEffect(() => {
    if (applyMode !== 'immediate') {
      return;
    }

    const hasValues = Object.keys(state.pendingValues).length > 0;
    const hasSorterSelection = Boolean(state.pendingSorterKey);
    const hasSelections = hasValues || hasSorterSelection;

    if (!hasAutoAppliedRef.current) {
      hasAutoAppliedRef.current = true;

      if (!hasSelections) {
        return;
      }
    }

    onApply?.(
      buildApplyPayload(filters, state.pendingValues, sorterOptions, state.pendingSorterKey),
    );
  }, [applyMode, filters, onApply, sorterOptions, state.pendingSorterKey, state.pendingValues]);

  const selectFilter = useCallback(
    (filterKey: string) => {
      dispatch({ type: 'SELECT_FILTER', payload: { filterKey } });
      onTabChange?.(filterKey, state.pendingValues);
    },
    [onTabChange, state.pendingValues],
  );

  const setPendingValue = useCallback(
    (filterKey: string, value: DiscoveryFilterValue | undefined) => {
      dispatch({
        type: 'SET_PENDING_VALUE',
        payload: { filterKey, value, syncApplied: shouldSyncApplied },
      });
    },
    [shouldSyncApplied],
  );

  const setPendingSorter = useCallback(
    (sorterKey: string | null) => {
      dispatch({
        type: 'SET_PENDING_SORTER',
        payload: { sorterKey, syncApplied: shouldSyncApplied },
      });
    },
    [shouldSyncApplied],
  );

  const clearFilter = useCallback(
    (filterKey: string) => {
      dispatch({ type: 'CLEAR_FILTER', payload: { filterKey, syncApplied: shouldSyncApplied } });
    },
    [shouldSyncApplied],
  );

  const clearAll = useCallback(() => {
    dispatch({
      type: 'CLEAR_ALL',
      payload: { syncApplied: true, resetSorterKey: resolvedSorterKey },
    });
    // dispatch({ type: 'CLEAR_ALL', payload: { syncApplied: shouldSyncApplied } });

    // if (shouldSyncApplied) {
    //   onClearAll?.();
    // }

    onClearAll?.();
  }, [onClearAll, resolvedSorterKey, shouldSyncApplied]);

  const applyFilters = useCallback(() => {
    dispatch({ type: 'APPLY_PENDING' });
    onApply?.(
      buildApplyPayload(filters, state.pendingValues, sorterOptions, state.pendingSorterKey),
    );
  }, [filters, onApply, sorterOptions, state.pendingSorterKey, state.pendingValues]);

  const contextValue = useMemo<DiscoveryContextValue>(
    () => ({
      ...state,
      filters,
      sorterOptions,
      applyMode,
      layout,
      selectFilter,
      setPendingValue,
      setPendingSorter,
      clearFilter,
      clearAll,
      applyFilters,
    }),
    [
      state,
      filters,
      sorterOptions,
      applyMode,
      layout,
      selectFilter,
      setPendingValue,
      setPendingSorter,
      clearFilter,
      clearAll,
      applyFilters,
    ],
  );

  return <DiscoveryContext.Provider value={contextValue}>{children}</DiscoveryContext.Provider>;
};

export const useDiscovery = (): DiscoveryContextValue => {
  const context = useContext(DiscoveryContext);
  if (!context) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('useDiscovery must be used within a DiscoveryProvider');
    }
    return fallbackContext;
  }
  return context;
};
